/**
 * Tab "Purchase" của /purchase/control — Access `frmProcurementMainSubFormLocalP`.
 * List (frmProcurementQryLocalP → public.mv_local_p) + 4 nút toolbar (theo Left):
 * Forced close · Re-open · Filling Docs · Print - Liquid.
 * Dùng template dataList chuẩn (/convert-table): FilterBar + Search + per-column ▽
 * + Columns menu (persist `procurement`/columns/procurement-purchase) + freeze contract_no
 * + row-select toggle.
 */
import BlockIcon from '@mui/icons-material/Block'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso, type TableVirtuosoHandle } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import {
  useListLocalPQuery,
  useReopenLocalPMutation,
  useToggleFillingDocsLocalPMutation,
} from '../../store/api/localPApi'
import type { LocalPListItem } from '../../types/api'
import type { ProcurementFilters } from '../ProcurementPage'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useAppSelector } from '../../store'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { titleCaseName } from '../../utils/text'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import ProcurementForcedCloseDialog from './ProcurementForcedCloseDialog'
import ProcurementLiquidPrintDialog from './ProcurementLiquidPrintDialog'

interface Props {
  filters: ProcurementFilters
}

type FilterKind = 'search'
type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof LocalPListItem
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/** Pull-everything-once page size — same convention as SaleContract ContractListView. */
const ALL_ROWS_PAGE_SIZE = 1_000_000

/** Cột có Total ở footer = đúng các cột Access đặt `AggregateType=0` (Sum) trong
 *  datasheet frmProcurementQryLocalP (TotalsRow=1). Cột giá/premium/average KHÔNG sum. */
const SUM_KEYS: ReadonlySet<string> = new Set([
  'quantity_kg', 'no_of_bag', 'received', 'received_bag', 'pending', 'tolerance',
  'logistic_cost', 'total_lot', 'lot_fixed', 'lot_to_fix', 'kg_fixed', 'kg_to_fix',
  // FX KHÔNG sum (chị Đẹp chốt — là tỷ giá, cộng vô nghĩa).
])

/** Freeze the Purchase No. identifier column when scrolling horizontally (/convert-table §7.6). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contract_no'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { contract_no: 140 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { contract_no: 120 }

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

// Thứ tự + caption theo `ColumnOrder` của frmProcurementQryLocalP (Access datasheet,
// 0..57 + trader/date/person ở cuối). `width` = default per cột; user drag-resize override.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'ctrl_filldoc_txt', label: 'Filling Docs', filter: 'checkboxes', width: 130 }, // 0
  { key: 'contract_no', label: 'Purchase No.', width: 140 }, // 1 (frozen)
  { key: 'contract_date', label: 'Contract Date', kind: 'date', width: 120 }, // 2
  { key: 'supplier_code', label: 'Supplier Code', filter: 'checkboxes', width: 140 }, // 3
  { key: 'npe_ref', label: 'NPE Purchase No.', width: 145 }, // 4
  { key: 'seller_ref', label: "Seller's Ref", width: 120 }, // 5
  { key: 'supplier_name', label: 'Seller', wrap: true, width: 180 }, // 6
  { key: 'shipper_name', label: 'Supplier', wrap: true, width: 160 }, // 7
  { key: 'quantity_kg', label: 'Quantity', kind: 'number', align: 'right', width: 120 }, // 8 (Total ~9 digits)
  { key: 'no_of_bag', label: 'Bags', kind: 'number', align: 'right', width: 100 }, // 9 (Total ~7 digits)
  { key: 'quality_code', label: 'Quality', filter: 'checkboxes', width: 95 }, // 10
  { key: 'outright_price', label: 'Price - Outright', kind: 'number', align: 'right', width: 135 }, // 11
  { key: 'ptbf_diffs', label: 'Price - Diff', kind: 'number', align: 'right', width: 110 }, // 12
  { key: 'vat_txt', label: 'Appliable VAT', filter: 'checkboxes', width: 140 }, // 13
  { key: 'latest_delivery', label: 'Delivery Latest', kind: 'date', width: 135 }, // 14
  { key: 'received', label: 'Received (KG)', kind: 'number', align: 'right', width: 125 }, // 15 (Total ~9 digits)
  { key: 'received_bag', label: 'Received (BAG)', kind: 'number', align: 'right', width: 125 }, // 16 (Total ~7 digits + 2dp)
  { key: 'pending', label: 'Pending', kind: 'number', align: 'right', width: 110 }, // 17 (Total ~7 digits)
  { key: 'tolerance', label: 'Tolerance', kind: 'number', align: 'right', width: 110 }, // 18 (Total ~7 digits)
  { key: 'note', label: 'Notes', wrap: true, width: 200 }, // 19
  { key: 'forced_close_text', label: 'Is Forced Close?', filter: 'checkboxes', width: 160 }, // 20
  { key: 'forced_close_date', label: 'Close Date', kind: 'date', width: 110 }, // 21
  { key: 'delivery_place', label: 'Delivery Place', filter: 'checkboxes', width: 150 }, // 22
  { key: 'certification', label: 'Certification', filter: 'checkboxes', width: 140 }, // 23
  { key: 'me_or_ib', label: 'ME or IB', filter: 'checkboxes', width: 100 }, // 24
  { key: 'license_cert_no', label: 'License Cert No.', width: 145 }, // 25
  { key: 'packaging', label: 'Packaging', filter: 'checkboxes', width: 110 }, // 26
  { key: 'contract_type', label: 'Contract Type', filter: 'checkboxes', width: 140 }, // 27
  { key: 'price_unit', label: 'Price Unit', filter: 'checkboxes', width: 115 }, // 28
  { key: 'futures_month', label: 'Futures Month', filter: 'checkboxes', width: 140 }, // 29
  { key: 'fixation_call', label: 'Fixation Call', width: 120 }, // 30
  { key: 'fx', label: 'FX', kind: 'number', align: 'right', width: 90 }, // 31 (no total)
  { key: 'logistic_cost', label: 'Logistics costs', kind: 'number', align: 'right', width: 135 }, // 32
  { key: 'premium_vs_g2_5percent', label: 'Quality Premium', kind: 'number', align: 'right', width: 135 }, // 33
  { key: 'cert_premium', label: 'Cert premium', kind: 'number', align: 'right', width: 110 }, // 34
  { key: 'package_premium', label: 'Package premium', kind: 'number', align: 'right', width: 130 }, // 35
  { key: 'hedged_price', label: 'Hedged Price', kind: 'number', align: 'right', width: 110 }, // 36
  { key: 'implied_diffs', label: 'Implied Diffs', kind: 'number', align: 'right', width: 120 }, // 37
  { key: 'diffs_g2_fob_bulk', label: 'G2 FOB Diff Bulk', kind: 'number', align: 'right', width: 145 }, // 38
  { key: 'total_lot', label: 'Total lot', kind: 'number', align: 'right', width: 90 }, // 39
  { key: 'lot_fixed', label: 'Lot fixed', kind: 'number', align: 'right', width: 90 }, // 40
  { key: 'lot_to_fix', label: 'Lot to fix', kind: 'number', align: 'right', width: 90 }, // 41
  { key: 'avg_futures_price', label: 'Avg. Futures Price', kind: 'number', align: 'right', width: 160 }, // 42
  { key: 'kg_fixed', label: 'Kg fixed', kind: 'number', align: 'right', width: 120 }, // 43 (Total ~9 digits)
  { key: 'kg_to_fix', label: 'Kg to fix', kind: 'number', align: 'right', width: 100 }, // 44
  { key: 'avg_price2', label: 'Avg. Price2', kind: 'number', align: 'right', width: 110 }, // 45
  { key: 'delivery_term', label: 'Delivery Term', filter: 'checkboxes', width: 140 }, // 46
  { key: 'delivery_place_code', label: 'Delivery Place Code', width: 165 }, // 47
  { key: 'issued_text', label: 'Issued', filter: 'checkboxes', width: 90 }, // 48
  { key: 'sent_mail_text', label: 'Sent mail', filter: 'checkboxes', width: 110 }, // 49
  { key: 'received_mail_text', label: 'Received mail', filter: 'checkboxes', width: 140 }, // 50
  { key: 'sent_paper_text', label: 'Sent paper', filter: 'checkboxes', width: 115 }, // 51
  { key: 'received_paper_text', label: 'Received paper', filter: 'checkboxes', width: 145 }, // 52
  { key: 'intermediary_text', label: 'Intermediary ?', filter: 'checkboxes', width: 145 }, // 53
  { key: 'contract_shipper_intermediary', label: 'Contract ref (Shipper w Intermediary)', width: 200 }, // 54
  { key: 'shipper_code', label: 'Supplier Code (Shipper)', width: 190 }, // 55
  { key: 'ptbfdiffs_shipper_intermediary', label: 'PTBF Diffs (Shipper w Intermediary)', kind: 'number', align: 'right', width: 160 }, // 56
  { key: 'outright2', label: 'Outright price (Shipper w Intermediary)', kind: 'number', align: 'right', width: 180 }, // 57
  { key: 'trader_in_charge_name', label: 'Trader In Charge', width: 145 }, // 58
  { key: 'date_update', label: 'Updated Date', kind: 'datetime', width: 140 }, // 59
  { key: 'person_update', label: 'Updated Person', width: 160 }, // 60
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: LocalPListItem[], key: keyof LocalPListItem): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

/** Cột tên riêng nguồn cp_vn → Title Case (DB lưu UPPERCASE "TUẤN HƯƠNG BẢN"). */
const NAME_KEYS: ReadonlySet<string> = new Set(['supplier_name', 'shipper_name'])

const cellValue = (row: LocalPListItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (NAME_KEYS.has(String(col.key))) return titleCaseName(val)
  return String(val)
}

type ConfirmKind = 'reopen' | 'filling-docs'

export default function ProcurementPurchaseListView({ filters }: Props) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const { canEdit } = usePagePermission()
  const isAdmin = useAppSelector((s) => !!s.auth.user?.is_admin)
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<string>()

  const [forcedCloseOpen, setForcedCloseOpen] = useState(false)
  const [liquidOpen, setLiquidOpen] = useState(false)
  // Chốt contract lúc bấm Print-Liquid — KHÔNG truyền selectedContractNo (live) cho
  // dialog, kẻo đổi dòng chọn sau đó sẽ re-trigger fetch + tự mở form (bug đã gặp).
  const [liquidContractNo, setLiquidContractNo] = useState('')
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; message: string } | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof LocalPListItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.purchase.list')

  const filterPopover = useFilterPopover<FilterKind>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof LocalPListItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'procurement',
    prefKey: 'columns',
    subKey: 'procurement-purchase',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'procurement.purchase.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error } = useListLocalPQuery(
    {
      delivery_latest_from: filters.startDate || undefined,
      delivery_latest_to: filters.endDate || undefined,
      is_forced_close:
        filters.isForcedClose === 'yes' ? true : filters.isForcedClose === 'no' ? false : undefined,
      page: 1,
      page_size: ALL_ROWS_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: true, skip: filters.startDate > filters.endDate },
  )

  const [reopen, { isLoading: reopenLoading }] = useReopenLocalPMutation()
  const [toggleFillingDocs, { isLoading: fillingLoading }] = useToggleFillingDocsLocalPMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => SUM_KEYS.has(String(col.key)))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = useMemo(
    () =>
      visibleColumns.reduce((acc, col) => {
        const key = String(col.key)
        const w = getWidth(key) ?? col.width ?? (FROZEN_KEYS.has(key) ? FROZEN_FALLBACK_WIDTH[key] : 100)
        return acc + w
      }, 0),
    [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof LocalPListItem) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<LocalPListItem | null>(
    () => (selectedId == null ? null : rows.find((r) => r.contract_no === selectedId) ?? null),
    [rows, selectedId],
  )

  // Access frmProcurementQryLocalP.Form_Load → `DoCmd.GoToRecord , , acLast` = record CUỐI.
  // → auto-select dòng cuối + scroll xuống 1 lần sau khi list load; KHÔNG đè lựa chọn thủ công.
  const virtuosoRef = useRef<TableVirtuosoHandle>(null)
  const didAutoSelectRef = useRef(false)
  useEffect(() => {
    if (isFetching || didAutoSelectRef.current) return
    if (rows.length > 0) {
      setSelectedId(rows[rows.length - 1].contract_no)
      didAutoSelectRef.current = true
    }
  }, [isFetching, rows, setSelectedId])

  // Access: mọi nút mở đầu bằng check `Form![subForm]!contract_no` → cần row đang chọn.
  const requireRow = (): LocalPListItem | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }
  const isForced = (row: LocalPListItem) => String(row.forced_close_text ?? '') === 'Y'

  const handleForcedClose = () => {
    const row = requireRow()
    if (!row) return
    if (isForced(row)) {
      snack.warning('This contract is forced close!')
      return
    }
    setForcedCloseOpen(true)
  }

  const handleReopen = () => {
    const row = requireRow()
    if (!row) return
    // Access btnUnforced: check admin (DCount public_users is_admin=1) TRƯỚC forced-state.
    if (!isAdmin) {
      snack.warning('This account does not have permission. Please contact the administrator!')
      return
    }
    if (!isForced(row)) {
      snack.warning('This contract is not forced close!')
      return
    }
    setConfirm({ kind: 'reopen', message: 'Do you want to unforce close This contract ?' })
  }

  const handleFillingDocs = () => {
    const row = requireRow()
    if (!row) return
    const message =
      String(row.ctrl_filldoc_txt ?? '') === 'Y'
        ? 'Do you want to uncheck the completed documents?'
        : 'Do you want to check the completed documents?'
    setConfirm({ kind: 'filling-docs', message })
  }

  const handlePrintLiquid = () => {
    const row = requireRow()
    if (!row) return
    if (!isForced(row)) {
      snack.warning('This contract is not forced close!')
      return
    }
    setLiquidContractNo(String(row.contract_no))
    setLiquidOpen(true)
  }

  const runConfirm = async () => {
    if (!confirm || !selectedRow) {
      setConfirm(null)
      return
    }
    const contractNo = String(selectedRow.contract_no)
    try {
      if (confirm.kind === 'reopen') {
        await reopen(contractNo).unwrap()
        snack.success('Successfully Unforced')
      } else {
        await toggleFillingDocs(contractNo).unwrap()
        snack.success('Successfully Updated')
      }
    } catch (e) {
      snack.error(getErrorMessage(e))
    } finally {
      setConfirm(null)
    }
  }

  const selectedContractNo = selectedRow ? String(selectedRow.contract_no) : ''
  const selectedPending = selectedRow && selectedRow.pending != null ? Number(selectedRow.pending) : null

  // Enable per-nút theo guard Access (dòng đang chọn phải thỏa điều kiện của nút đó).
  const selForced = !!selectedRow && isForced(selectedRow)
  const canForcedClose = !!selectedRow && canEdit && !selForced // chỉ khi CHƯA forced
  const canReopen = !!selectedRow && canEdit && selForced // chỉ khi ĐÃ forced
  const canFillingDocs = !!selectedRow && canEdit // toggle 2 chiều — chỉ cần có dòng
  const canPrintLiquid = !!selectedRow && canEdit && selForced // chỉ khi ĐÃ forced
  // Filling Docs là toggle → đổi text theo state hiện tại của dòng (Y = đã check → Uncheck).
  const fillingChecked = !!selectedRow && String(selectedRow.ctrl_filldoc_txt ?? '') === 'Y'
  const fillingLabel = fillingChecked ? 'Uncheck Docs' : 'Check Docs'

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!canForcedClose}
              onClick={handleForcedClose}
              startIcon={<BlockIcon sx={{ fontSize: 16 }} />}
              title="Forced close"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Forced close</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!canReopen || reopenLoading}
              onClick={handleReopen}
              startIcon={<LockOpenIcon sx={{ fontSize: 16 }} />}
              title="Re-open"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Re-open</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              sx={actionToolbarBtnSx}
              disabled={!canFillingDocs || fillingLoading}
              onClick={handleFillingDocs}
              startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
              title={fillingLabel}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{fillingLabel}</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!canPrintLiquid}
              onClick={handlePrintLiquid}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - Liquid"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print - </Box>Liquid
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e: MouseEvent<HTMLElement>) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Could not load Purchase list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract no, supplier, quality…"
              label="Search Purchase"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Purchase list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading || isFetching ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Purchase data"
              description="Try adjusting the Delivery Lastest date range or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              ref={virtuosoRef}
              style={{ height: '100%' }}
              data={rows}
              initialTopMostItemIndex={rows.length > 0 ? { index: rows.length - 1, align: 'end' } : 0}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: LocalPListItem) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && row.contract_no === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row) toggleSelect(row.contract_no)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <ProcurementForcedCloseDialog
        open={forcedCloseOpen}
        contractNo={selectedContractNo}
        pending={selectedPending}
        canEdit={canEdit}
        onClose={() => setForcedCloseOpen(false)}
        onSaved={() => setSelectedId(null)}
      />

      <ProcurementLiquidPrintDialog
        open={liquidOpen}
        contractNo={liquidContractNo}
        onClose={() => setLiquidOpen(false)}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.kind === 'reopen' ? 'Unforced close' : 'Filling Docs'}
        message={confirm?.message ?? ''}
        onConfirm={() => void runConfirm()}
        onClose={() => setConfirm(null)}
      />
    </Box>
  )
}
